import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Award, Zap, Clock, TrendingUp, Users, Target, ChevronRight, Star } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Benefit Card Component
const BenefitCard = ({ icon: Icon, title, description }) => (
  <div 
    className="card p-8 h-full"
    style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
      style={{ background: "var(--primary-100)" }}
    >
      <Icon size={28} style={{ color: "white" }} />
    </div>
    <h3 
      className="text-xl font-bold mb-3"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h3>
    <p style={{ color: "var(--text-200)", lineHeight: "1.6" }}>{description}</p>
  </div>
);

// Main Why Choose Us Page Component
const WhyChooseUsPage = () => {
  const benefits = [
    {
      icon: Zap,
      title: "Helps turn trades faster",
      description: "Streamlined workflow from appraisal to market means less time sitting on stock."
    },
    {
      icon: Target,
      title: "Reduces missed details",
      description: "Guided appraisal fields ensure consistent, accurate valuations every time."
    },
    {
      icon: Clock,
      title: "Cuts listing time",
      description: "No more re-entering data. One click pushes your machine to multiple platforms."
    },
    {
      icon: TrendingUp,
      title: "Gets stock to market sooner",
      description: "Professional listings created instantly from appraisal data you already captured."
    },
    {
      icon: Users,
      title: "Keeps teams focused",
      description: "Your people spend time with customers, not wrestling with admin and multiple systems."
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.1) 0%, transparent 70%)"
            }}
          />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ background: "var(--primary-100)" }}
              >
                <Award size={20} style={{ color: "white" }} />
                <span className="text-sm font-semibold" style={{ color: "white" }}>The Better Choice</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Why Choose <span className="text-gradient">MachineryMarketed</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed exists for one simple reason: to help dealerships{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>move machinery faster</span> and{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>run tighter operations</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Built By Dealership People Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div 
                className="card p-10 mb-12"
                style={{ 
                  background: "var(--background-200)", 
                  borderColor: "rgba(15, 95, 255, 0.2)",
                  borderLeftWidth: "4px",
                  borderLeftColor: "var(--primary-300)"
                }}
              >
                <p 
                  className="text-xl mb-6"
                  style={{ color: "var(--text-100)", lineHeight: "1.8" }}
                >
                  This platform wasn't dreamed up in a boardroom or by people who've never stood in a yard. 
                  It was built by <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>dealership people</span> who understand 
                  trade-ins, valuations, aged stock, and the pressure of keeping metal moving while everything 
                  else competes for your time.
                </p>
                <p 
                  className="text-lg"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  We focus on the parts of the business that matter most — accurate appraisals, clear valuations, 
                  and fast, professional marketing — and connect them in one clean workflow. No double handling. 
                  No chasing photos. No re-entering the same information over and over again.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Works With Your Systems */}
        <section className="py-16" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Star size={48} style={{ color: "var(--primary-300)", margin: "0 auto 24px" }} />
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Works With What You Have
              </h2>
              <p 
                className="text-lg mb-8"
                style={{ color: "var(--text-200)", lineHeight: "1.8" }}
              >
                MachineryMarketed works alongside the systems you already use. We don't force you to rip 
                anything out or change how you sell. We simply remove friction, tighten the process, and 
                give your team better visibility and control.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                What Dealers Gain
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                Real benefits that make a difference to your bottom line
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <BenefitCard key={index} {...benefit} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing Statement */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Bottom Line
              </h2>
              <p 
                className="text-xl mb-8"
                style={{ color: "var(--text-200)", lineHeight: "1.8" }}
              >
                At the end of the day, <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>moving metal is the game</span>. 
                MachineryMarketed helps you do it better, faster, and with fewer headaches.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/about" className="btn-outline">
                  About Us
                </Link>
                <Link to="/how-it-works" className="btn-outline">
                  How It Works
                </Link>
                <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
                  Request a Demo <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WhyChooseUsPage;
